import { ref, reactive, readonly } from 'vue'

export interface Notification {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message?: string
  duration?: number
  persistent?: boolean
}

const notifications = ref<Notification[]>([])
let notificationId = 0

export function useNotifications() {
  const addNotification = (notification: Omit<Notification, 'id'>): string => {
    const id = `notification-${++notificationId}`
    const newNotification: Notification = {
      id,
      duration: 5000, // Default 5 seconds
      ...notification,
    }

    notifications.value.push(newNotification)

    // Auto-remove notification after duration (unless persistent)
    if (!newNotification.persistent && newNotification.duration) {
      setTimeout(() => {
        removeNotification(id)
      }, newNotification.duration)
    }

    return id
  }

  const removeNotification = (id: string) => {
    const index = notifications.value.findIndex(n => n.id === id)
    if (index > -1) {
      notifications.value.splice(index, 1)
    }
  }

  const clearAll = () => {
    notifications.value = []
  }

  const showSuccess = (title: string, message?: string, duration?: number): string => {
    return addNotification({
      type: 'success',
      title,
      message,
      duration: duration ?? 10000, // Default 15 seconds for success notifications
    })
  }

  const showError = (title: string, message?: string, persistent?: boolean): string => {
    return addNotification({
      type: 'error',
      title,
      message,
      persistent,
      duration: persistent ? undefined : 16000, // Errors show longer
    })
  }

  const showWarning = (title: string, message?: string, duration?: number): string => {
    return addNotification({
      type: 'warning',  
      title,
      message,
      duration,
    })
  }

  const showInfo = (title: string, message?: string, duration?: number): string => {
    return addNotification({
      type: 'info',
      title,
      message, 
      duration,
    })
  }

  return {
    notifications: readonly(notifications),
    addNotification,
    removeNotification,
    clearAll,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  }
} 